import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api } from "../../api";
import { useI18n } from "../../lib/i18n";
import { desktopState, readPrefs, requestDesktop, savePrefs, type DesktopState, type NotifyPrefs } from "../../lib/notify";
import { currentEndpoint, pushSupported, pushUiState, subscribePush, unsubscribePush, type PushUiState } from "../../lib/push";
import { playNotification } from "../../socket";
import { REMINDER_CHOICES } from "../../lib/unanswered";
import { Section, SettingRow, Switch } from "./common";
import { TelegramSection } from "./TelegramSection";
import { DESKTOP_DOWNLOAD_URL, isDesktopApp } from "../../lib/desktop";

/**
 * How the console gets this operator's attention, per browser. The desktop
 * permission is asked for only from the button here, never on page load.
 */
export function NotificationsTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.notify;
  const [prefs, setPrefs] = useState<NotifyPrefs>(readPrefs);
  const [permission, setPermission] = useState<DesktopState>(desktopState);

  /**
   * Web Push, which unlike the rest of this screen is not a browser
   * preference: the subscription lives on the server, so the state has to be
   * read back rather than remembered here. `null` until that read answers,
   * which is what keeps the row from flickering on for a moment.
   */
  const [push, setPush] = useState<{ publicKey: string | null; devices: number } | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);

  const loadPush = useCallback(async () => {
    try {
      const state = await api.pushState();
      setPush({ publicKey: state.publicKey, devices: state.devices });
      setSubscribed((await currentEndpoint()) !== null);
    } catch {
      // An older server has no such route; push simply does not appear.
      setPush({ publicKey: null, devices: 0 });
    }
  }, []);

  useEffect(() => {
    void loadPush();
  }, [loadPush]);

  function update(next: Partial<NotifyPrefs>): void {
    const merged = { ...prefs, ...next };
    setPrefs(merged);
    savePrefs(merged);
  }

  async function enableDesktop(on: boolean): Promise<void> {
    if (!on) {
      update({ desktop: false });
      return;
    }
    const state = permission === "granted" ? "granted" : await requestDesktop();
    setPermission(state);
    update({ desktop: state === "granted" });
  }

  async function enablePush(on: boolean): Promise<void> {
    setPushError(null);
    setPushBusy(true);
    try {
      if (!on) {
        await unsubscribePush();
      } else if (!push?.publicKey || !(await subscribePush(push.publicKey))) {
        setPushError(desktopState() === "denied" ? t.pushDenied : t.pushFailed);
      }
      setPermission(desktopState());
      await loadPush();
    } finally {
      setPushBusy(false);
    }
  }

  function test(): void {
    if (prefs.sound) playNotification();
    if (prefs.desktop && permission === "granted") {
      try {
        new Notification(t.testTitle, { body: t.testBody, tag: "test" });
      } catch {
        /* see lib/notify */
      }
    }
  }

  const pushState: PushUiState = pushUiState({
    configured: push?.publicKey != null,
    supported: pushSupported(),
    permission,
    subscribed,
  });
  const pushHint: Record<PushUiState, string> = {
    hidden: t.pushHint,
    unsupported: t.pushUnsupported,
    denied: t.pushDenied,
    off: t.pushHint,
    on: t.pushOn(push?.devices ?? 0),
  };

  return (
    <>
    <Section title={t.browserTitle} intro={t.intro}>
      <Switch label={t.sound} hint={t.soundHint} checked={prefs.sound} onChange={(v) => update({ sound: v })} />
      <Switch label={t.tabTitle} hint={t.tabTitleHint} checked={prefs.title} onChange={(v) => update({ title: v })} />
      <Switch
        label={t.desktop}
        hint={
          permission === "unsupported" ? t.desktopUnsupported : permission === "denied" ? t.desktopDenied : t.desktopHint
        }
        checked={prefs.desktop && permission === "granted"}
        disabled={permission === "unsupported" || permission === "denied"}
        onChange={(v) => void enableDesktop(v)}
      />
      {pushState !== "hidden" && (
        <>
          <Switch
            label={t.push}
            hint={pushBusy ? t.pushEnabling : pushHint[pushState]}
            checked={pushState === "on"}
            disabled={pushBusy || pushState === "unsupported" || pushState === "denied"}
            onChange={(v) => void enablePush(v)}
          />
          {pushError && (
            <div class="alert error" role="status">
              {pushError}
            </div>
          )}
        </>
      )}
      <SettingRow title={t.unanswered} hint={t.unansweredHint} htmlFor="notify-unanswered">
        <select
          id="notify-unanswered"
          value={String(prefs.unansweredAfter)}
          onChange={(e) => update({ unansweredAfter: Number(e.currentTarget.value) })}
        >
          {REMINDER_CHOICES.map((n) => (
            <option key={n} value={String(n)}>
              {n === 0 ? t.unansweredOff : t.unansweredMinutes(n)}
            </option>
          ))}
        </select>
      </SettingRow>
      <SettingRow title={t.testRow} hint={t.testHint}>
        <button class="btn sm" onClick={test}>
          {t.test}
        </button>
      </SettingRow>
      {!isDesktopApp() && (
        <SettingRow title={t.desktopApp} hint={t.desktopAppHint}>
          <a class="btn sm" href={DESKTOP_DOWNLOAD_URL} download="AgentlyDesk-Setup.exe">
            {t.desktopAppDownload}
          </a>
        </SettingRow>
      )}
    </Section>
    <TelegramSection isAdmin={isAdmin} />
    </>
  );
}
